import React from 'react';
import { getMyotomeMappingForMuscle } from '../data/emgClinicalCriteria';
import type { Patient } from '../types/patient';
import type { NCSTestReferenceValues } from '../types/ncs';
import type { ClinicalReport, EMGMuscleResult } from '../services/jsonReportGenerator';
import { buildClinicalImpression, type DiagnosticAnalysisResult } from '../services/clinicalImpressionBuilder';

interface PrintableEdxReportProps {
  report: ClinicalReport;
  patient: Patient;
  analysis?: DiagnosticAnalysisResult;
}

const display = (value: unknown, suffix = ''): string => {
  if (value === undefined || value === null || value === '') return '-';
  return `${value}${suffix}`;
};

const formatReferenceValues = (referenceValues?: NCSTestReferenceValues): string => {
  if (!referenceValues) return 'No documentada';
  const parts = [
    referenceValues.latency?.max !== undefined ? `Lat <= ${referenceValues.latency.max}` : '',
    referenceValues.amplitude?.min !== undefined ? `Amp >= ${referenceValues.amplitude.min}` : '',
    referenceValues.velocity?.min !== undefined ? `VC >= ${referenceValues.velocity.min}` : '',
    referenceValues.duration?.max !== undefined ? `Dur <= ${referenceValues.duration.max}` : '',
    referenceValues.fWaveLatency?.max !== undefined ? `F <= ${referenceValues.fWaveLatency.max}` : '',
  ].filter(Boolean);
  return parts.length > 0 ? parts.join('; ') : 'No documentada';
};

const emgMappingLabel = (muscle: EMGMuscleResult): string => {
  const mapping = getMyotomeMappingForMuscle(muscle.muscleName);
  if (!mapping) return '-';
  const roots = mapping.roots.length > 0 ? mapping.roots.join('/') : '-';
  return `${roots}; ${mapping.peripheralNerve}`;
};

const PrintableEdxReport: React.FC<PrintableEdxReportProps> = ({ report, patient, analysis }) => {
  const impression = buildClinicalImpression(report, analysis);
  const ncsResults = report.electrophysiologicalData.ncs?.results || [];
  const emg = report.electrophysiologicalData.emg;
  const specialStudies = report.electrophysiologicalData.specialStudies;

  return (
    <article className="edx-print-report bg-white text-slate-950 rounded-lg shadow-xl border border-slate-200 p-6 print:p-0 print:shadow-none print:border-0">
      <header className="border-b border-slate-300 pb-4 mb-5">
        <h1 className="text-2xl font-bold tracking-wide text-slate-950">Reporte de Electroneuromiografía</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm mt-3">
          <p><strong>Paciente:</strong> {patient.firstName} {patient.lastName}</p>
          <p><strong>ID:</strong> {report.patient.id}</p>
          <p><strong>Edad:</strong> {report.patient.demographics.age} años</p>
          <p><strong>Sexo:</strong> {report.patient.demographics.sex}</p>
          <p><strong>Fecha del estudio:</strong> {new Date(report.metadata.studyDateTime).toLocaleString()}</p>
          <p><strong>Laboratorio:</strong> {display(report.metadata.laboratoryName)}</p>
          <p className="md:col-span-2"><strong>Indicación:</strong> {impression.indication}</p>
        </div>
      </header>

      <section className="mb-5">
        <h2 className="text-lg font-semibold text-slate-950 border-b border-slate-200 pb-1 mb-2">Impresión Diagnóstica</h2>
        <p className="font-medium">{impression.impression}</p>
        <p className="mt-2 text-sm">{impression.clinicalCorrelation}</p>
        {impression.compatiblePatterns.length > 0 && (
          <ul className="mt-2 list-disc pl-5 text-sm">
            {impression.compatiblePatterns.map(pattern => (
              <li key={pattern.name}>
                {pattern.name} ({Math.round(pattern.confidence * 100)}%, urgencia {pattern.urgencyLevel})
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mb-5">
        <h2 className="text-lg font-semibold text-slate-950 border-b border-slate-200 pb-1 mb-2">Condiciones Técnicas y Seguridad</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <p><strong>Equipo:</strong> {[report.metadata.equipment?.manufacturer, report.metadata.equipment?.model, report.metadata.equipment?.serialNumber].filter(Boolean).join(' ') || '-'}</p>
          <p><strong>Electrodo de aguja:</strong> {report.metadata.needleElectrode}</p>
          <p><strong>Marcapasos/dispositivo:</strong> {report.metadata.safetyScreening.pacemakerOrImplantableDevice ? 'Sí' : 'No'}</p>
          <p><strong>Anticoagulación:</strong> {report.metadata.safetyScreening.anticoagulation ? 'Sí' : 'No'}</p>
          <p className="md:col-span-2"><strong>Limitaciones:</strong> {impression.limitations}</p>
          <p className="md:col-span-2"><strong>Cambio de intervalo:</strong> {report.metadata.intervalComparison?.summary || 'No documentado'}</p>
        </div>
      </section>

      <section className="mb-5">
        <h2 className="text-lg font-semibold text-slate-950 border-b border-slate-200 pb-1 mb-2">Neuroconducciones (NCS)</h2>
        {ncsResults.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="edx-report-table w-full text-xs border-collapse">
              <thead>
                <tr>
                  <th>Nervio</th>
                  <th>Lado</th>
                  <th>Tipo</th>
                  <th>Estimulación</th>
                  <th>Registro</th>
                  <th>Dist.</th>
                  <th>Lat.</th>
                  <th>Pico</th>
                  <th>Amp.</th>
                  <th>VC</th>
                  <th>Temp.</th>
                  <th>Referencia</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {ncsResults.map(result => (
                  <tr key={result.id}>
                    <td>{result.nerve}</td>
                    <td>{result.side}</td>
                    <td>{result.type}</td>
                    <td>{display(result.stimulationSite || result.site)}</td>
                    <td>{display(result.recordingSite)}</td>
                    <td>{display(result.distance, result.distanceUnit || ' cm')}</td>
                    <td>{display(result.latency, result.latencyUnit || ' ms')}</td>
                    <td>{display(result.peakLatency, ' ms')}</td>
                    <td>{display(result.amplitude, result.amplitudeUnit || '')}</td>
                    <td>{display(result.velocity, ' m/s')}</td>
                    <td>{display(result.temperature, ' °C')}</td>
                    <td>{formatReferenceValues(result.referenceValues)}</td>
                    <td>{result.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm">No se registraron neuroconducciones.</p>
        )}
      </section>

      <section className="mb-5">
        <h2 className="text-lg font-semibold text-slate-950 border-b border-slate-200 pb-1 mb-2">Electromiografía con Aguja (EMG)</h2>
        {emg?.status === 'performed' && emg.muscles.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="edx-report-table w-full text-xs border-collapse">
              <thead>
                <tr>
                  <th>Músculo</th>
                  <th>Lado</th>
                  <th>Miotoma / nervio</th>
                  <th>Insercional</th>
                  <th>Espontánea</th>
                  <th>PUM</th>
                  <th>Reclutamiento</th>
                  <th>Notas</th>
                </tr>
              </thead>
              <tbody>
                {emg.muscles.map((muscle, index) => (
                  <tr key={`${muscle.muscleName}-${muscle.side}-${index}`}>
                    <td>{muscle.muscleName}</td>
                    <td>{muscle.side}</td>
                    <td>{emgMappingLabel(muscle)}</td>
                    <td>{muscle.insertionalActivity}</td>
                    <td>{muscle.spontaneousActivitySummary}</td>
                    <td>amp {muscle.mupAmplitude} µV; dur {muscle.mupDuration} ms; polif {muscle.mupPolyphasia}%</td>
                    <td>{muscle.recruitmentPattern}</td>
                    <td>{muscle.interpretationNotes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm">{emg?.status === 'skipped' ? 'EMG no realizada.' : 'Sin datos EMG interpretables.'}</p>
        )}
      </section>

      {specialStudies && !specialStudies.notPerformed && specialStudies.tests.length > 0 && (
        <section className="mb-5">
          <h2 className="text-lg font-semibold text-slate-950 border-b border-slate-200 pb-1 mb-2">Estudios Especiales</h2>
          <table className="edx-report-table w-full text-xs border-collapse">
            <thead>
              <tr>
                <th>Estudio</th>
                <th>Tipo</th>
                <th>Lado</th>
                <th>Valores</th>
                <th>Estado</th>
                <th>Hallazgos</th>
              </tr>
            </thead>
            <tbody>
              {specialStudies.tests.map(test => (
                <tr key={test.id}>
                  <td>{test.name}</td>
                  <td>{test.type}</td>
                  <td>{test.side}</td>
                  <td>{Object.entries(test.values || {}).map(([key, value]) => `${key}: ${value}${test.unit || ''}`).join(', ') || '-'}</td>
                  <td>{test.status}</td>
                  <td>{test.findings?.join('. ') || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {impression.recommendations.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-slate-950 border-b border-slate-200 pb-1 mb-2">Recomendaciones</h2>
          <ul className="list-disc pl-5 text-sm">
            {impression.recommendations.map(rec => <li key={rec}>{rec}</li>)}
          </ul>
        </section>
      )}
    </article>
  );
};

export default PrintableEdxReport;
